import React, { useRef, useState, useEffect } from "react";
import "./TextEditor.css";
import {
  BiUndo,
  BiRedo,
  BiBold,
  BiUnderline,
  BiItalic,
  BiStrikethrough,
  BiAlignLeft,
  BiAlignMiddle,
  BiAlignRight,
  BiAlignJustify,
  BiListOl,
  BiListUl,
  BiLink,
  BiUnlink,
} from "react-icons/bi";
import { FiCode } from "react-icons/fi";

const TextEditor = ({ onChange }) => {
  const contentRef = useRef(null);
  const [filename, setFilename] = useState("untitled");
  const [showCode, setShowCode] = useState(false);

  const formatDoc = (cmd, value = null) => {
    document.execCommand(cmd, false, value);
  };

  const addLink = () => {
    const url = prompt("Enter URL:");
    if (url) formatDoc("createLink", url);
  };

  const fileHandle = (action) => {
    const content = contentRef.current;
    if (action === "new") {
      content.innerHTML = "";
      setFilename("untitled");
    } else if (action === "txt") {
      const blob = new Blob([content.innerText], { type: "text/plain" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}.txt`;
      link.click();
    } else if (action === "pdf") {
      import("html2pdf.js").then((html2pdf) => {
        html2pdf.default().from(content).save(`${filename}.pdf`);
      });
    }
  };

  const toggleCode = () => {
    const content = contentRef.current;
    if (!showCode) {
      content.textContent = content.innerHTML;
      content.setAttribute("contenteditable", false);
    } else {
      content.innerHTML = content.textContent;
      content.setAttribute("contenteditable", true);
    }
    setShowCode(!showCode);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const clipboard = e.clipboardData || window.clipboardData;
    const html = clipboard.getData("text/html");
    const text = clipboard.getData("text/plain");
    if (html) {
      document.execCommand("insertHTML", false, html);
    } else {
      document.execCommand("insertText", false, text);
    }
  };

  useEffect(() => {
    const content = contentRef.current;

    const handleHover = () => {
      const links = content.querySelectorAll("a");
      links.forEach((link) => {
        link.addEventListener("mouseenter", () => {
          content.setAttribute("contenteditable", false);
          link.setAttribute("target", "_blank");
        });
        link.addEventListener("mouseleave", () => {
          if (!showCode) content.setAttribute("contenteditable", true);
        });
      });
    };

    content.addEventListener("mouseenter", handleHover);
    return () => content.removeEventListener("mouseenter", handleHover);
  }, [showCode]);

  useEffect(() => {
    const handleInput = () => {
      if (onChange) {
        onChange(contentRef.current?.innerHTML || "");
      }
    };

    const current = contentRef.current;
    current.addEventListener("input", handleInput);
    return () => current.removeEventListener("input", handleInput);
  }, [onChange]);

  return (
    <div className="text-editor-container">
      <div className="toolbar">
        <div className="head">
          {/* <input
            type="text"
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
          /> */}
          {/* <select
            onChange={(e) => {
              fileHandle(e.target.value);
              e.target.selectedIndex = 0;
            }}
            defaultValue=""
          >
            <option value="" hidden disabled>
              File
            </option>
            <option value="new">New File</option>
            <option value="txt">Save as TXT</option>
            <option value="pdf">Save as PDF</option>
          </select> */}
          <select
            onChange={(e) => {
              formatDoc("formatBlock", e.target.value);
              e.target.selectedIndex = 0;
            }}
            defaultValue=""
          >
            <option value="" hidden disabled>
              Format
            </option>
            <option value="h1">H1</option>
            <option value="h2">H2</option>
            <option value="h3">H3</option>
            <option value="p">Paragraph</option>
          </select>
          <select
            onChange={(e) => {
              formatDoc("fontSize", e.target.value);
              e.target.selectedIndex = 0;
            }}
            defaultValue=""
          >
            <option value="" hidden disabled>
              Font Size
            </option>
            <option value="1">XS</option>
            <option value="2">Small</option>
            <option value="3">Normal</option>
            <option value="4">Medium</option>
            <option value="5">Large</option>
            <option value="6">XL</option>
            <option value="7">Huge</option>
          </select>
          <div className="color">
            <span>Color</span>
            <input
              type="color"
              onInput={(e) => formatDoc("foreColor", e.target.value)}
            />
          </div>
          <div className="color">
            <span>Test Highlighter</span>
            <input
              type="color"
              onInput={(e) => formatDoc("hiliteColor", e.target.value)}
            />
          </div>
        </div>

        <div className="btn-toolbar">
          <button type="button" onClick={() => formatDoc("undo")}>
            <BiUndo />
          </button>
          <button type="button" onClick={() => formatDoc("redo")}>
            <BiRedo />
          </button>
          <button type="button" onClick={() => formatDoc("bold")}>
            <BiBold />
          </button>
          <button type="button" onClick={() => formatDoc("underline")}>
            <BiUnderline />
          </button>
          <button type="button" onClick={() => formatDoc("italic")}>
            <BiItalic />
          </button>
          <button type="button" onClick={() => formatDoc("strikeThrough")}>
            <BiStrikethrough />
          </button>
          <button type="button" onClick={() => formatDoc("justifyLeft")}>
            <BiAlignLeft />
          </button>
          <button type="button" onClick={() => formatDoc("justifyCenter")}>
            <BiAlignMiddle />
          </button>
          <button type="button" onClick={() => formatDoc("justifyRight")}>
            <BiAlignRight />
          </button>
          <button type="button" onClick={() => formatDoc("justifyFull")}>
            <BiAlignJustify />
          </button>
          <button type="button" onClick={() => formatDoc("insertOrderedList")}>
            <BiListOl />
          </button>
          <button
            type="button"
            onClick={() => formatDoc("insertUnorderedList")}
          >
            <BiListUl />
          </button>
          <button type="button" onClick={addLink}>
            <BiLink />
          </button>
          <button type="button" onClick={() => formatDoc("unlink")}>
            <BiUnlink />
          </button>
          {/* <button type="button" onClick={toggleCode}>
            <FiCode />
          </button> */}
        </div>
      </div>

      <div
        id="content"
        ref={contentRef}
        contentEditable={!showCode}
        spellCheck={false}
        onPaste={handlePaste}
        style={{
          background: "#fff",
          padding: "16px",
          minHeight: "200px",
          overflow: "auto",
          border: "1px solid #ccc",
        }}
      />
    </div>
  );
};

export default TextEditor;
